import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BehaviorSubject, Observable, map, switchMap } from 'rxjs';
import { FormsModule } from '@angular/forms';

import { AssetsService, AssetDto } from '../../api/assets.service';

@Component({
	selector: 'of-media-library-page',
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		FormsModule,
	],
	styleUrls: ['./media-library.page.scss'],
	template: `
		<section class="media-shell" *ngIf="vm$ | async as vm; else loading">
			<header class="media-header">
				<div>
					<p class="kicker">Workspace</p>
					<h2>Media Library</h2>
					<p class="muted small">World {{ vm.worldId }}</p>
				</div>
				<div class="header-actions">
					<button type="button" class="btn-outline" (click)="refresh()">Refresh</button>
				</div>
			</header>

			<div class="media-card">
				<div class="media-panel">
					<div class="panel-head">
						<h4>Browse</h4>
					</div>
					<div class="media-grid">
						<label class="forge-field">
							<span>Media Type (optional)</span>
							<input type="text" [(ngModel)]="filters.mediaType" name="mediaType" placeholder="images / maps / pdfs" />
						</label>
						<label class="forge-field">
							<span>Purpose (optional)</span>
							<input type="text" [(ngModel)]="filters.purpose" name="purpose" />
						</label>
					</div>
					<div class="media-actions">
						<button type="button" class="btn-primary-gradient" (click)="refresh()">Apply</button>
					</div>
				</div>

				<div class="media-panel">
					<div class="panel-head">
						<h4>Upload (register)</h4>
						<p class="muted small" style="margin:0;">
							Note: assets must include <code>worlds/{{ vm.worldId }}/</code> in URL or R2 key to be linkable.
						</p>
					</div>
					<div class="media-grid">
						<label class="forge-field">
							<span>Name (optional)</span>
							<input type="text" [(ngModel)]="createModel.name" name="createName" />
						</label>
						<label class="forge-field">
							<span>Type</span>
							<input type="text" [(ngModel)]="createModel.type" name="createType" placeholder="images / maps / pdfs" />
						</label>
						<label class="forge-field wide">
							<span>URL</span>
							<input type="text" [(ngModel)]="createModel.url" name="createUrl" />
						</label>
						<label class="forge-field">
							<span>R2 Key (optional)</span>
							<input type="text" [(ngModel)]="createModel.r2Key" name="createR2Key" />
						</label>
						<label class="forge-field">
							<span>Mime Type (optional)</span>
							<input type="text" [(ngModel)]="createModel.mimeType" name="createMimeType" />
						</label>
						<label class="forge-field wide">
							<span>Purpose (optional)</span>
							<input type="text" [(ngModel)]="createModel.purpose" name="createPurpose" />
						</label>
					</div>
					<div class="media-actions">
						<button
							type="button"
							class="btn-primary-gradient"
							(click)="create()"
							[disabled]="creating || !createModel.type || !createModel.url"
						>
							{{ creating ? 'Creating...' : 'Create' }}
						</button>
					</div>
				</div>

				<div class="media-panel" *ngIf="vm.assets.length > 0; else empty">
					<div class="panel-head">
						<h4>Assets ({{ vm.assets.length }})</h4>
					</div>
					<div class="media-list">
						<div class="media-row" *ngFor="let a of vm.assets">
							<div class="media-row__main">
								<div class="media-row__name">{{ a.name ?? '(unnamed)' }}</div>
								<div class="media-row__meta">
									{{ a.type }} · {{ a.purpose ?? '—' }} · Updated {{ a.updatedAt | date:'short' }}
								</div>
								<div class="media-row__meta small">Asset {{ a.id }}</div>
							</div>
							<div class="media-row__actions">
								<a class="btn-outline" [href]="a.url" target="_blank" rel="noreferrer">Open</a>
								<button type="button" class="btn-outline" (click)="remove(a)">Delete</button>
							</div>
						</div>
					</div>
				</div>

				<ng-template #empty>
					<div class="empty-state">No assets found.</div>
				</ng-template>
			</div>
		</section>

		<ng-template #loading>
			<section class="media-shell">
				<p class="support-text">Loading...</p>
			</section>
		</ng-template>
	`,
})
export class MediaLibraryPage {
	worldId = '';
	filters: { mediaType: string; purpose: string } = { mediaType: '', purpose: '' };
	createModel: {
		name: string;
		type: string;
		purpose: string;
		r2Key: string;
		url: string;
		mimeType: string;
	} = {
		name: '',
		type: 'images',
		purpose: '',
		r2Key: '',
		url: '',
		mimeType: '',
	};

	creating = false;

	private readonly refresh$ = new BehaviorSubject<void>(undefined);

	vm$: Observable<{ worldId: string; assets: AssetDto[] }>;

	constructor(route: ActivatedRoute, private readonly assets: AssetsService) {
		this.worldId = route.parent!.snapshot.paramMap.get('worldId') ?? '';

		const worldId$ = route.parent!.paramMap.pipe(map((p) => p.get('worldId') ?? ''));
		this.vm$ = this.refresh$.pipe(
			switchMap(() => worldId$),
			switchMap((worldId) => {
				this.worldId = worldId;
				return this.assets.list(worldId, {
					mediaType: this.filters.mediaType || null,
					purpose: this.filters.purpose || null,
				});
			}),
			map((res) => ({ worldId: this.worldId, assets: res.assets }))
		);
	}

	refresh() {
		this.refresh$.next();
	}

	create() {
		this.creating = true;
		this.assets
			.create(this.worldId, {
				name: this.createModel.name || null,
				type: this.createModel.type,
				purpose: this.createModel.purpose || null,
				r2Key: this.createModel.r2Key || null,
				url: this.createModel.url,
				mimeType: this.createModel.mimeType || null,
			})
			.subscribe({
				next: () => {
					this.creating = false;
					this.createModel = {
						name: '',
						type: this.createModel.type,
						purpose: '',
						r2Key: '',
						url: '',
						mimeType: '',
					};
					this.refresh();
				},
				error: () => {
					this.creating = false;
				},
			});
	}

	remove(asset: AssetDto) {
		this.assets.delete(this.worldId, asset.id).subscribe({
			next: () => this.refresh(),
		});
	}
}
